/* Step 5 — Stress Test. Ported from the prototype's scenariosBody(). Three tabs:
   AI Strategy Review ("AI-CEO" defensibility test), Growth simulation (Monte-Carlo
   ARR at month 24) and Exit paths. Data loads from GET /deal/:id/scenarios; if none
   exists it can be generated via POST .../scenarios/run. "Share with founder" pushes
   outputs into the founder's Research Aid via POST .../share. */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import DealStrip from '../../components/DealStrip';
import TabRow from '../../components/TabRow';
import { Loading, EmptyState } from '../../components/States';
import { useToast } from '../../components/Toast';
import { BASE, levelColor } from '../../helpers';
import { LABELS } from '../../data/config';
import { fetchScenarios, runScenarios, shareDeal, ok } from '../../api';

const Dot = ({ level }) => <span className="finding__dot" style={{ background: levelColor(level) }} />;
const aiceo = t => (t || '').replaceAll('{AICEO}', LABELS.aiceo);

export default function Scenarios({ deal }) {
	const { tab: tabParam } = useParams();
	const navigate = useNavigate();
	const showToast = useToast();
	const tab = ['strategy', 'growth', 'exit'].includes(tabParam) ? tabParam : 'strategy';
	const [S, setS] = useState(null);
	const [status, setStatus] = useState('loading');    // loading | ready | empty | generating
	const [sharing, setSharing] = useState(false);

	const load = useCallback(async () => {
		setStatus('loading');
		const data = await fetchScenarios(deal.id);
		if (!data) { setStatus('empty'); setS(null); return; }
		setS(data); setStatus('ready');
	}, [deal.id]);
	useEffect(() => { load(); }, [load]);

	const generate = async () => {
		setStatus('generating');
		const res = await runScenarios(deal.id);
		if (ok(res)) { setS(res.body); setStatus('ready'); showToast('Stress test generated'); }
		else setStatus('empty');
	};

	const shareWithFounder = async () => {
		if (sharing) return;
		setSharing(true);
		const res = await shareDeal(deal.id, { target: 'founder' });
		setSharing(false);
		if (!ok(res)) return;
		showToast(`${LABELS.aiceo} shared with ${deal.name}`);
		setTimeout(() => showToast(`Added to ${deal.name.split(' ')[0]}'s Research Aid on Diligentsia`, true), 900);
	};

	if (status === 'loading') return (<main className="page screen"><DealStrip deal={deal} stepId="scenarios" /><div style={{ marginTop: 14 }}><Loading label="Loading stress test…" /></div></main>);
	if (status === 'empty' || status === 'generating') return (
		<main className="page screen">
			<DealStrip deal={deal} stepId="scenarios" />
			<div className="card" style={{ marginTop: 14, textAlign: 'center', padding: 28 }}>
				<div className="card__label" style={{ marginBottom: 8 }}>{LABELS.scenarios}</div>
				<div style={{ fontSize: 13.5, color: 'var(--dlg-muted)', marginBottom: 14 }}>Run Intelligentsia's strategy, growth and exit simulations for {deal.name}.</div>
				<button className="pill pill--primary" onClick={generate} disabled={status === 'generating'}>{status === 'generating' ? 'Generating…' : `Generate ${LABELS.scenarios}`}</button>
			</div>
		</main>
	);

	const strategy = S.strategy || {}, growth = S.growth || {}, exit = S.exit || {};
	const TABS = [
		{ id: 'strategy', name: LABELS.aiceo }, { id: 'growth', name: 'Growth simulation' }, { id: 'exit', name: 'Exit paths' },
	];

	return (
		<main className="page screen">
			<DealStrip deal={deal} stepId="scenarios" />
			<TabRow baseHref={`${BASE}/deal/${deal.id}/scenarios`} activeTab={tab} tabs={TABS} />

			{tab === 'strategy' && (
				<>
					<span className="chip chip--blue" style={{ marginBottom: 12 }}>✦ {strategy.chip}</span>
					<div className="grid grid--split45" style={{ marginTop: 12 }}>
						<div className="card plan"><div className="card__label" style={{ marginBottom: 4 }}>Founder's plan</div>
							{(strategy.founder || []).map((r, i) => <div key={i} className="plan__row">{r}</div>)}</div>
						<div className="card plan plan--alt"><div className="card__label" style={{ marginBottom: 4 }}>Intelligentsia's alternative</div>
							{(strategy.alternative || []).map((r, i) => <div key={i} className="plan__row">{r}</div>)}</div>
					</div>
					<div className="card" style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
						<div>
							<div className="card__label" style={{ marginBottom: 6 }}>Defensibility verdict</div>
							<div style={{ fontSize: 14.5, color: 'var(--dlg-ink-soft)', maxWidth: 700 }} dangerouslySetInnerHTML={{ __html: strategy.verdict || '' }} />
						</div>
						<div className="verdict-dots">{(strategy.verdictDots || []).map((d, i) => <span key={i} style={{ background: levelColor(d) }} />)}</div>
					</div>
					<div style={{ display: 'flex', gap: 10, marginTop: 4, flexWrap: 'wrap', alignItems: 'center' }}>
						<button className="pill pill--primary" onClick={shareWithFounder} disabled={sharing}>{sharing ? 'Sharing…' : 'Share with founder →'}</button>
						<button className="pill pill--outline" onClick={() => navigate(`${BASE}/deal/${deal.id}/scenarios/growth`)}>Run growth simulation</button>
					</div>
				</>
			)}

			{tab === 'growth' && (
				<>
					<span className="chip chip--blue" style={{ marginBottom: 12 }}>✦ {growth.chip}</span>
					<div className="card" style={{ marginTop: 12 }}>
						<div className="card__label" style={{ marginBottom: 14 }}>Projected ARR in 24 months</div>
						{(growth.rows || []).map((r, i) => (
							<div key={i} className="simrow">
								<div className="simrow__head">
									<span className="simrow__name">{aiceo(r.name)}</span>
									<div className="simrow__bar"><div className="simrow__fill" style={{ width: `${r.pct}%`, background: r.color }} /></div>
									<span className="simrow__arr">{r.arr}</span>
								</div>
								<div className="simrow__sub">{r.sub}</div>
							</div>
						))}
					</div>
					<div className="grid grid--split45" style={{ marginTop: 12 }}>
						<div className="card"><div className="card__label" style={{ marginBottom: 8 }}>What moves the number</div>
							{(growth.movers || []).map((m, i) => <div key={i} className="finding"><Dot level={m.level} /><span>{m.text}</span></div>)}</div>
						<div className="card"><div className="card__label" style={{ marginBottom: 8 }}>Read for the committee</div>
							<div style={{ fontSize: 13.5, lineHeight: 1.65, color: 'var(--dlg-ink-soft)' }}>{aiceo(growth.committee)}</div></div>
					</div>
					<div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
						<button className="pill pill--primary" onClick={() => navigate(`${BASE}/deal/${deal.id}/scenarios/exit`)}>Continue to exit paths →</button>
						<button className="pill pill--outline" onClick={shareWithFounder} disabled={sharing}>Share with founder</button>
					</div>
				</>
			)}

			{tab === 'exit' && (
				<>
					<span className="chip chip--blue" style={{ marginBottom: 12 }}>✦ {exit.chip}</span>
					<div className="exitgrid" style={{ marginTop: 12 }}>
						{(exit.paths || []).map((x, i) => (
							<div key={i} className="card">
								<div className="exitcard__head"><span className="exitcard__name">{x.name}</span><span className={`chip ${x.tagCls}`} style={{ marginLeft: 'auto', flex: 'none' }}>{x.tag}</span></div>
								<div className="exitcard__text">{x.text}</div>
								<div className="exitcard__ret" dangerouslySetInnerHTML={{ __html: x.ret || '' }} />
							</div>
						))}
					</div>
					<div className="card">
						<div className="card__label" style={{ marginBottom: 6 }}>What this means for the deal</div>
						<div style={{ fontSize: 14, color: 'var(--dlg-ink-soft)' }} dangerouslySetInnerHTML={{ __html: exit.meaning || '' }} />
					</div>
					<div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
						<button className="pill pill--primary" onClick={() => navigate(`${BASE}/deal/${deal.id}/memo`)}>Generate IC memo →</button>
						<button className="pill pill--outline" onClick={shareWithFounder} disabled={sharing}>Share with founder</button>
					</div>
				</>
			)}
		</main>
	);
}
